import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';
import VerticalListCheckboxItem from '../VerticalListCheckboxItem';
import TitleTypography from '../MUI/TitleTypography';

import useStyles from './makeStyles';

const AdditionalCoverages = (props) => {
  const {
    type,
    coverages,
    amounts,
    selectedCoverages,
    onCoverageChange,
    hidePrice,
    isFetchingPrice,
    hasNarrowView,
    getSingleCoverageAmount,
    getSingleCoverageDiscount,
    getSingleCoveragePrice,
  } = props;

  const { t } = useTranslation();
  const [activeDesc, setActiveDesc] = useState(null);
  const classes = useStyles();

  const coveragesData = useMemo(() => {
    const coveragesWithAmounts = !!amounts.length
      && coverages.map((cov) => (
        {
          ...cov,
          additionalData: [
            {
              key: 'amount',
              value: getSingleCoverageAmount(cov.id),
              isAmount: true,
            },
          ],
        }
      ));

    return coveragesWithAmounts || coverages;
  }, [amounts.length, coverages, getSingleCoverageAmount]);

  const handleCoverageChange = useCallback((coverageId, checked) => {
    if (checked) {
      onCoverageChange({
        ...selectedCoverages,
        [coverageId]: { ...selectedCoverages[coverageId], active: true },
      });
    } else {
      onCoverageChange({
        ...selectedCoverages,
        [coverageId]: { ...selectedCoverages[coverageId], active: false },
      });
    }
    // Uncomment this block if CASCO will be implemented
    // if (coverageId === CMTPL_CASCO_PLANS.standard.coverages.damage.id) {
    //   dispatch(setCoverageFiles([]));
    // }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCoverageChange, selectedCoverages]);

  const handleResolveCoverageId = useCallback((coverageId) => coverageId, []);

  return (
    <Box sx={{ mt: '2rem' }}>
      <TitleTypography className={classes.sectionTitle}>{t(`${type}.additionalCoverages`)}</TitleTypography>
      <div>
        {coveragesData.map((coverage) => (
          <VerticalListCheckboxItem
            key={coverage.id}
            resource={`${type}.additionalCoverageTypes`}
            price={getSingleCoveragePrice(handleResolveCoverageId(coverage.id))}
            discount={getSingleCoverageDiscount(coverage.id)}
            item={coverage}
            hasActiveDesc={activeDesc === coverage.id}
            setActiveDesc={setActiveDesc}
            hidePrice={hidePrice}
            isFetchingPrice={isFetchingPrice}
            isChecked={
              !!selectedCoverages
              && !!selectedCoverages[coverage.id]
              && selectedCoverages[coverage.id].active
            }
            onItemChange={handleCoverageChange}
            hasNarrowView={hasNarrowView}
          />
        ))}
      </div>
    </Box>
  );
};

AdditionalCoverages.propTypes = {
  type: PropTypes.string.isRequired,
  coverages: PropTypes.arrayOf(PropTypes.shape({})),
  amounts: PropTypes.arrayOf(PropTypes.shape({})),
  contractData: PropTypes.shape({
    promoCodes: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  selectedCoverages: PropTypes.shape({
    CA: PropTypes.shape({ active: PropTypes.bool, id: PropTypes.string }),
  }).isRequired,
  onCoverageChange: PropTypes.func.isRequired,
  hidePrice: PropTypes.bool,
  isFetchingPrice: PropTypes.bool,
  hasNarrowView: PropTypes.bool,
  getSingleCoverageAmount: PropTypes.func,
  getSingleCoverageDiscount: PropTypes.func,
  getSingleCoveragePrice: PropTypes.func,
};

AdditionalCoverages.defaultProps = {
  coverages: [],
  amounts: [],
  hidePrice: false,
  isFetchingPrice: false,
  hasNarrowView: false,
  getSingleCoverageAmount: () => { },
  getSingleCoverageDiscount: () => { },
  getSingleCoveragePrice: () => { },
};

export default AdditionalCoverages;
